import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import api, { AU_STATES, STATUS_STYLES, shortDate, money } from "@/lib/api";

const STATUS_COLORS = {
  Active: "#16A34A",
  "New Customer": "#2563EB",
  "Returning Customer": "#7C3AED",
  "Follow-up Required": "#D97706",
  "No Sale This Month": "#F59E0B",
  Inactive: "#64748B",
  "Declining Customer": "#DC2626",
};

export default function TerritoryMap() {
  const [customers, setCustomers] = useState([]);
  const [state, setState] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    api.get("/customers").then(r => setCustomers(r.data));
  }, []);

  const shown = useMemo(
    () => customers.filter(c =>
      c.latitude != null && c.longitude != null &&
      (!state || c.state === state) &&
      (!status || c.status === status)
    ),
    [customers, state, status]
  );

  return (
    <div className="space-y-4" data-testid="map-page">
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">Territory Map</h1>
          <p className="text-slate-600 mt-1">{shown.length} stores plotted • coloured by activity</p>
        </div>
        <div className="flex gap-2">
          <select value={state} onChange={(e) => setState(e.target.value)} className="h-11 px-3 border border-slate-300 rounded-lg" data-testid="map-state">
            <option value="">All States</option>
            {AU_STATES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-11 px-3 border border-slate-300 rounded-lg" data-testid="map-status">
            <option value="">All Statuses</option>
            {Object.keys(STATUS_COLORS).map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 text-xs">
        {Object.entries(STATUS_COLORS).map(([label, color]) => (
          <div key={label} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full inline-block" style={{ background: color }} />
            <span className="text-slate-600">{label}</span>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" style={{ height: "70vh" }}>
        <MapContainer center={[-28.5, 133.5]} zoom={4} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {shown.map(c => (
            <CircleMarker
              key={c.id}
              center={[c.latitude, c.longitude]}
              radius={9}
              pathOptions={{ color: "#fff", weight: 2, fillColor: STATUS_COLORS[c.status] || "#94A3B8", fillOpacity: 0.9 }}
            >
              <Popup>
                <div style={{ minWidth: 200 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{c.name}</div>
                  <div style={{ color: "#64748B", fontSize: 12 }}>{c.town}, {c.state}</div>
                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[c.status] || "badge-inactive"}`}>{c.status}</span>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: "#64748B" }}>
                    Last order: {shortDate(c.last_order_date)}<br />
                    Last visit: {shortDate(c.last_visit_date)}
                  </div>
                  <Link to={`/customers/${c.id}`} style={{ display: "inline-block", marginTop: 8, color: "#003DA5", fontWeight: 600, fontSize: 12 }}>
                    Open profile →
                  </Link>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}
